import { ApolloError, useQuery } from '@apollo/client';
import { productKeys } from 'domain/product-subscription';
import { addTime } from '@perch-labs/warbler';
import { THIRTY_DAYS } from 'domain/statement';
import { GET_STATEMENTS } from 'pages/billing/billing.api';
import { useProductSubscription } from './use-product-subscription.hook';
import { useStatement } from './use-statement.hook';
import { usePlan } from './use-plan.hook';

const subscriptionActive = subscription => {
  return subscription.data?.status === 'active';
};

type Plan = GetPlanForUsePlanQuery['plan'];
export type Statement = GetStatementsQuery['plan']['statements'][number];

export type BillingStatusData = {
  allStatements?: Statement[];
  hasSponsoredMembership: boolean;
  issuedStatements?: Statement[];
  mostRecentStatement?: {
    data: any;
    error?: ApolloError;
    loading: boolean;
  };
  paymentMethod?: Plan['paymentMethod'];
  trialEndDate?: Date;
  utilityName?: string;
  balances?: GetStatementsQuery['plan']['balances'];
  paymentLink?: GetStatementsQuery['plan']['paymentLink'];
};

interface Props {
  planId?: number;
}

export const useBillingStatus = ({ planId }: Props) => {
  const plan = usePlan({ planId });
  const sponsoredMembershipSubscription = useProductSubscription({
    productKey: productKeys.sponsoredMembership,
    planId,
  });
  const hasSponsoredMembership = subscriptionActive(
    sponsoredMembershipSubscription
  );

  const freeTrialSubscription = useProductSubscription({
    productKey: productKeys.freeTrial,
    planId,
  });
  const hasFreeTrial = subscriptionActive(freeTrialSubscription);
  const trialLength = freeTrialSubscription.data?.details.processLimit;
  const trialEndDate = addTime(freeTrialSubscription.data?.createdAt, {
    days: THIRTY_DAYS * trialLength,
  });

  const allStatements = useQuery<
    GetStatementsQuery,
    GetStatementsQueryVariables
  >(GET_STATEMENTS, {
    skip: !planId,
    variables: { planId: planId || -1 },
  });
  const mostRecentStatementId =
    allStatements.data?.plan.mostRecentStatement?.id;
  const mostRecentStatement = useStatement({
    id: mostRecentStatementId,
    skip: !mostRecentStatementId,
  });
  const balances = allStatements.data?.plan.balances;
  const paymentLink = allStatements.data?.plan.paymentLink;
  const { paymentMethod, utilityAccounts } = plan.data || {};
  const { statements } = allStatements.data?.plan || {};
  const issuedStatements = statements?.filter(
    statement => !!statement.issuedAt && statement.status !== 'void'
  );

  const error =
    allStatements.error ||
    mostRecentStatement.error ||
    sponsoredMembershipSubscription.error ||
    freeTrialSubscription.error;
  const loading =
    plan.loading ||
    allStatements.loading ||
    mostRecentStatement.loading ||
    sponsoredMembershipSubscription.loading ||
    freeTrialSubscription.loading;

  const data = {
    allStatements,
    hasSponsoredMembership,
    issuedStatements,
    mostRecentStatement,
    paymentMethod,
    trialEndDate: hasFreeTrial && trialEndDate,
    utilityName: utilityAccounts?.[0]?.utility.displayName,
    balances,
    paymentLink,
  };

  return { data, error, loading };
};
